import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  DocumentTextIcon,
  PencilSquareIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import { HsePageChrome } from "../../../../../components/dashboard/hse/HsePageChrome";
import {
  createDocumentFormCategory,
  fetchDocumentFormCategories,
  fetchDocumentFormTemplates,
  updateDocumentFormCategory,
} from "../../../../../api/documentForms";
import { PMDialog } from "../../../../../components/utils/PMDialog";

export const DocumentFormTemplatesList = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ id: null, name: "", description: "" });
  const [savingCategory, setSavingCategory] = useState(false);

  const loadCategories = useCallback(async () => {
    const res = await fetchDocumentFormCategories({ active: true });
    setCategories(res?.data || []);
  }, []);

  const loadTemplates = useCallback(async (q, cat) => {
    setLoading(true);
    const res = await fetchDocumentFormTemplates({
      query: q,
      per_page: 100,
      document_form_template_category_id: cat || undefined,
    });
    setTemplates(res?.data || []);
    setLoading(false);
  }, []);

  const debouncedLoad = useMemo(
    () => debounce((q, cat) => loadTemplates(q, cat), 250),
    [loadTemplates]
  );

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    debouncedLoad(query, categoryId);
    return () => debouncedLoad.cancel();
  }, [query, categoryId, debouncedLoad]);

  const saveCategory = async () => {
    setSavingCategory(true);
    const payload = {
      name: categoryForm.name,
      description: categoryForm.description,
      active: true,
    };
    const res = categoryForm.id
      ? await updateDocumentFormCategory(categoryForm.id, payload)
      : await createDocumentFormCategory(payload);
    setSavingCategory(false);
    if (res?.errors) return;
    setCategoryDialogOpen(false);
    setCategoryForm({ id: null, name: "", description: "" });
    loadCategories();
  };

  return (
    <HsePageChrome
      title={t("documentForms.templates.title")}
      subtitle={t("documentForms.templates.subtitle")}
    >
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCategoryId("")}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${
                !categoryId ? "bg-slate-900 text-white" : "bg-white text-slate-700 ring-1 ring-slate-200"
              }`}
            >
              {t("documentForms.categories.all")}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setCategoryId(String(cat.id))}
                className={`rounded-full px-4 py-2 text-sm font-semibold ${
                  categoryId === String(cat.id)
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-700 ring-1 ring-slate-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCategoryDialogOpen(true)}
              className="rounded-full px-4 py-2 text-sm font-semibold text-slate-600 ring-1 ring-dashed ring-slate-300"
            >
              + {t("documentForms.categories.new")}
            </button>
          </div>
          <div className="flex gap-2">
            <Link
              to="/dashboard/hse-ks/documents/form-archive"
              className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              {t("documentForms.archive.title")}
            </Link>
            <Link
              to="/dashboard/hse-ks/documents/templates/new"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
            >
              <PlusIcon className="h-4 w-4" />
              {t("documentForms.templates.new")}
            </Link>
          </div>
        </div>

        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-4">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("documentForms.search_placeholder")}
              className="w-full max-w-md rounded-full border border-slate-200 px-4 py-2 text-sm"
            />
          </div>
          {loading ? (
            <div className="px-6 py-12 text-sm text-slate-500">{t("Loading")}...</div>
          ) : templates.length === 0 ? (
            <div className="px-6 py-12 text-sm text-slate-500">{t("documentForms.templates.empty")}</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {templates.map((tpl) => (
                <li key={tpl.id} className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-start gap-3">
                    <DocumentTextIcon className="mt-0.5 h-5 w-5 text-slate-400" />
                    <div>
                      <div className="font-semibold text-slate-900">{tpl.title}</div>
                      {tpl.description ? (
                        <div className="mt-0.5 text-sm text-slate-500">{tpl.description}</div>
                      ) : null}
                      {tpl.category?.name ? (
                        <div className="mt-1 text-xs text-slate-400">{tpl.category.name}</div>
                      ) : null}
                      {tpl.forms_count > 0 ? (
                        <Link
                          to={`/dashboard/hse-ks/documents/form-archive?template_id=${tpl.id}`}
                          className="mt-2 inline-block text-xs font-semibold text-slate-600 hover:text-slate-900"
                        >
                          {t("documentForms.archive.view_for_template", { count: tpl.forms_count })}
                        </Link>
                      ) : null}
                    </div>
                  </div>
                  <Link
                    to={`/dashboard/hse-ks/documents/templates/${tpl.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    {t("Edit")}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <PMDialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        title={t("documentForms.categories.new")}
        size="sm"
      >
        <div className="space-y-3 px-1 py-2">
          <input
            type="text"
            value={categoryForm.name}
            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
            placeholder={t("documentForms.categories.name")}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
          <textarea
            value={categoryForm.description}
            onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
            placeholder={t("documentForms.categories.description")}
            rows={3}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="flex justify-end">
            <button
              type="button"
              disabled={savingCategory || !categoryForm.name}
              onClick={saveCategory}
              className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {t("Save")}
            </button>
          </div>
        </div>
      </PMDialog>
    </HsePageChrome>
  );
};
